import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Card, CardService, TaskTypeEnum } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { NextActivityFormComponent } from '../../task/activity-dialogs/next-activity-form/next-activity-form.component';

/**
 * Data the dialog can be opened with.
 *
 * `chainedFromOutcome` is set when the dialog is chained straight after an outcome was
 * registered: the salesperson just closed the deal's next action, so the very next
 * thing asked is what follows (RF-40, AC-21).
 */
export interface NextActionDialogData {
  card: Card;
  chainedFromOutcome?: boolean;
}

/**
 * Schedules the deal's next action (RF-12, RF-39, RF-40).
 *
 * `MatDialog` is used for the modal shell — that is one of the five allowed Material
 * uses — and the content inside is plain Hyper markup. Since E4 the fields are the shared
 * `NextActivityFormComponent` (SPEC-2026-10-crm-activities-cadence RF-01, RF-04): the seven
 * sales types and the date + time + "sem horário" field, the same the task form and the
 * conclusion dialog render.
 */
@Component({
  selector: 'app-next-action-dialog',
  templateUrl: './next-action-dialog.component.html',
  styleUrls: ['./next-action-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NextActionDialogComponent extends AbstractComponent implements OnInit {

  form!: FormGroup;
  saving = false;

  constructor(
    injector: Injector,
    private readonly cardService: CardService,
    private readonly dialogRef: MatDialogRef<NextActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NextActionDialogData,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    /**
     * Defaults to tomorrow WITHOUT a set time (E4 plan §11, point 3): this dialog was always
     * date-only, so "sem horário" on by default keeps that behaviour — a time is one click away.
     * A commitment due today is almost always a mistake in a dialog opened to plan the next step.
     */
    this.form = NextActivityFormComponent.buildForm(this.formBuilder, {
      type: TaskTypeEnum.CALL,
      moment: { dueAt: this.tomorrow(), untimed: true },
    });
  }

  get isChained(): boolean {
    return this.data?.chainedFromOutcome === true;
  }

  save(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    this.cardService
      .setNextAction(this.data.card._id, NextActivityFormComponent.toPayload(this.form.value))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Called on data arrival, not in ngOnInit: `t()` before the catalog loads
          // returns the raw key.
          this.alertService.success(this.t('kanban.nextAction.saved'));
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.saving = false;
          // The backend answers 409 when the deal already has a next action; its
          // message is already translated by the exception filter.
          this.alertService.error(error?.error?.message ?? this.t('kanban.nextAction.saveError'));
        },
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  private tomorrow(): Date {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }
}
